import { useQuery } from "@apollo/client";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useDebounce } from "react-use";
import { Text, Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis, type TooltipProps } from "recharts";
import { useSpinDelay } from "spin-delay";
import { type ChartVisibilityState } from "../BookingsForecast";
import { ChartDateNavigator } from "../ChartDateNavigator";
import { type IntervalType, CHART_BOOKING_STATUSES } from "./constants";
import {
  aggregateBookingsIntoBucketsForInterval,
  calculateEndDateForInterval,
  calculateTooltipData,
  getBucketCountForInterval,
  getYAxisWidth,
  type ChartBookingStatus,
  type ChartData,
  createEmptyBuckets,
  isBucketSelected,
  isChartBooking,
} from "./helpers";
import { IntervalSelector } from "./IntervalSelector";
import { StatusKeyPopover, StatusLegend } from "./StatusKey";
import { ErrorCard } from "@/components/ErrorCard";
import { Heading4 } from "@/components/Typography";
import { Card } from "@/components/ui/card";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { graphql } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { toIsoDate } from "@/lib/utils/dates";
import { getErrorMessage } from "@/lib/utils/errors";
import { type BookingFilters } from "@/routes/(authed)/_layout/bookings.route";

interface BookingsChartProps {
  visibility: ChartVisibilityState;
  setFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
  filters: BookingFilters;
  intervalType: IntervalType;
  dateRange: { start: Date; end: Date };
  onIntervalTypeChange: (intervalType: IntervalType) => void;
  referenceDate: Date;
  onNavigateNext: () => void;
  onNavigatePrevious: () => void;
  onResetToToday: () => void;
}

export function BookingsChart({
  visibility,
  setFilters,
  filters,
  intervalType,
  dateRange,
  onIntervalTypeChange,
  referenceDate,
  onNavigateNext,
  onNavigatePrevious,
  onResetToToday,
}: BookingsChartProps) {
  return (
    <div
      className={`
        grid
        transition-[grid-template-rows] duration-300 ease-in-out
        ${visibility === "expanded" ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}
      `}
    >
      <div className="overflow-hidden">
        <Card className={"m-4 p-4 compact:p-3 bg-transparent"}>
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Heading4>Forecast</Heading4>
                  <StatusKeyPopover className="hidden compact:inline-flex" />
                </div>
                <IntervalSelector selectedInterval={intervalType} onChange={onIntervalTypeChange} className="w-fit" />
              </div>
              {/* On wider screens the date navigator renders above the card instead */}
              <ChartDateNavigator
                referenceDate={referenceDate}
                intervalType={intervalType}
                onNavigateNext={onNavigateNext}
                onNavigatePrevious={onNavigatePrevious}
                onResetToToday={onResetToToday}
                className="hidden justify-between compact:flex"
              />
            </div>

            <BookingsChartDetails
              setFilters={setFilters}
              filters={filters}
              intervalType={intervalType}
              dateRange={dateRange}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

function BookingsChartDetails({
  setFilters,
  filters,
  intervalType,
  dateRange,
}: {
  setFilters: React.Dispatch<React.SetStateAction<BookingFilters>>;
  filters: BookingFilters;
  intervalType: IntervalType;
  dateRange: { start: Date; end: Date };
}) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const [debouncedDateRange, setDebouncedDateRange] = useState(dateRange);

  useDebounce(
    () => {
      setDebouncedDateRange(dateRange);
    },
    300,
    [dateRange.start, dateRange.end],
  );

  const { data, loading, previousData, error } = useQuery(bookingsChartQuery, {
    variables: {
      businessUnitIds: activeBusinessUnitId ? [activeBusinessUnitId] : undefined,
      plannedShippingDateAfter: toIsoDate(debouncedDateRange.start),
      plannedShippingDateBefore: toIsoDate(debouncedDateRange.end),
      status: CHART_BOOKING_STATUSES,
    },
    fetchPolicy: "cache-and-network",
  });

  const displayData = data || previousData;
  const isInitialLoad = !data && !previousData && loading;
  const isUpdating = Boolean(loading && previousData);
  const showLoadingOverlay = useSpinDelay(isUpdating, { delay: 100, minDuration: 300 });

  const bookings = removeNothings(displayData?.supplyChainBookings?.edges?.map((edge) => edge?.node) || []);

  const emptyBuckets = createEmptyBuckets({
    startDate: dateRange.start,
    intervalType,
    bucketCount: getBucketCountForInterval(intervalType),
  });

  const chartData = aggregateBookingsIntoBucketsForInterval({
    buckets: emptyBuckets,
    bookings: bookings.filter(isChartBooking),
    intervalType,
  });

  const yAxisWidth = getYAxisWidth(Math.max(0, ...chartData.map((bucket) => bucket.totalCount)));

  const handleBarClick = (data: ChartData[number]) => {
    const startDate = format(data.startDate, "yyyy-MM-dd");
    const endDate = calculateEndDateForInterval(data.startDate, intervalType);

    setFilters((prev) => {
      // If selected, remove the date filters by returning new filters without them
      if (
        isBucketSelected({
          bucket: data,
          dateOpenedBefore: prev.dateOpenedBefore,
          dateOpenedAfter: prev.dateOpenedAfter,
        })
      ) {
        const { dateOpenedAfter, dateOpenedBefore, ...remainingFilters } = prev;
        return remainingFilters;
      }

      // Otherwise, set the new date range
      return {
        ...prev,
        dateOpenedAfter: startDate,
        dateOpenedBefore: endDate,
      };
    });
  };

  if (error) {
    return <ErrorCard title={"Error loading chart data"} description={getErrorMessage(error)} />;
  }

  if (isInitialLoad) {
    return <BookingChartSkeleton />;
  }

  if (!displayData) {
    return <div className="h-80 w-full flex items-center justify-center text-muted-foreground">No data available</div>;
  }

  return (
    <div className="relative">
      {showLoadingOverlay && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      <ChartContainer config={chartConfig} className="h-80 w-full cursor-pointer">
        <BarChart
          data={chartData}
          accessibilityLayer
          className="cursor-pointer"
          margin={{ top: 30, right: 0, left: 0, bottom: 0 }}
          onClick={(data) => {
            const selectedBar = data?.activePayload?.[0]?.payload;
            if (!selectedBar) {
              return;
            }

            handleBarClick(selectedBar);
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={10} />
          <YAxis
            width={yAxisWidth}
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => value.toLocaleString()}
          />
          <ChartTooltip content={CustomTooltip} />
          {(Object.keys(chartConfig) as Array<keyof typeof chartConfig>).map((status) => (
            <Bar
              key={status}
              dataKey={`countsByStatus.${status}`}
              stackId="bookings"
              fill={chartConfig[status].color}
              radius={[4, 4, 0, 0]}
              className="cursor-pointer"
            >
              {chartData.map((bucket, index) => {
                return (
                  <Cell
                    key={`cell-${index}`}
                    opacity={
                      isBucketSelected({
                        bucket,
                        dateOpenedAfter: filters.dateOpenedAfter,
                        dateOpenedBefore: filters.dateOpenedBefore,
                      })
                        ? 1
                        : 0.7
                    }
                  />
                );
              })}
              <LabelList
                dataKey={(bucket) => {
                  return (bucket as unknown as ChartData[number]).totalCount;
                }}
                content={({ x, value, width }) => {
                  const xValue = typeof x === "number" && typeof width === "number" ? x + width / 2 : 0;
                  const displayValue = typeof value === "number" ? value.toLocaleString() : value;

                  return (
                    <Text x={xValue} dy={20} textAnchor="middle">
                      {displayValue}
                    </Text>
                  );
                }}
              />
            </Bar>
          ))}
        </BarChart>
      </ChartContainer>
      <StatusLegend className="flex compact:hidden" />
    </div>
  );
}

export function BookingChartSkeleton() {
  const barHeights = ["60%", "50%", "65%", "45%"];
  return (
    <div className="w-full flex flex-col px-8 compact:px-2">
      <div className="h-80 flex items-end justify-between pb-8">
        {barHeights.map((height, index) => (
          <Skeleton key={index} className="w-[20%] animate-pulse" style={{ height }} />
        ))}
      </div>
      <div className="h-10 w-full flex compact:hidden justify-center items-center">
        <Skeleton className="w-3/4 h-4 animate-pulse rounded-md" />
      </div>
    </div>
  );
}

// In Recharts, the prop `payload` represents an array of `Payload` objects, which can be confusing.
// For better clarity, we rename `payload` to `payloads` (plural) in our destructuring,
// making it clearer that we're working with multiple Payload objects.
const CustomTooltip = ({ active, payload: payloads }: TooltipProps<number, string>) => {
  if (!active || !payloads?.length) return null;

  const { countTotalAnimals, statusBreakdowns } = calculateTooltipData(payloads);

  if (countTotalAnimals === 0) return null;

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md">
      <div className="border-b pb-2 mb-2">
        <div className="font-medium">Total: {countTotalAnimals} animals</div>
      </div>
      {statusBreakdowns?.map((breakdown) => {
        if (!breakdown.countAnimals || breakdown.countAnimals <= 0) return null;

        return (
          <div key={breakdown.status} className="flex items-center justify-between w-full">
            <span className="flex items-center gap-2 min-w-[140px]">
              <span className={"inline-block w-3 h-3 rounded-full"} style={{ backgroundColor: breakdown.color }} />
              {breakdown.label}:
            </span>
            <span className="font-medium">{breakdown.countAnimals} animals</span>
          </div>
        );
      })}
    </div>
  );
};

export const chartConfig = {
  DRAFT: {
    label: "Draft",
    color: "hsl(var(--chart-draft))",
  },
  PLANNED: {
    label: "Planned",
    color: "hsl(var(--chart-planned))",
  },
  BOOKED: {
    label: "Booked",
    color: "hsl(var(--chart-booked))",
  },
  SHIPPED: {
    label: "Shipped",
    color: "hsl(var(--chart-shipped))",
  },
  AWAITING_PAYMENT: {
    label: "Awaiting Payment",
    color: "hsl(var(--chart-awaiting-payment))",
  },
  PAID: {
    label: "Paid",
    color: "hsl(var(--chart-paid))",
  },
  ARCHIVED: {
    label: "Archived",
    color: "hsl(var(--chart-archived))",
  },
  CLOSED: {
    label: "Closed",
    color: "hsl(var(--chart-closed))",
  },
} as const satisfies Record<ChartBookingStatus, { label: string; color: string }>;

export const bookingsChartQuery = graphql(`
  query BookingsChart(
    $businessUnitIds: [ID!]
    $plannedShippingDateBefore: Date
    $plannedShippingDateAfter: Date
    $status: [SupplyChainBookingStatusEnumType!]
  ) {
    supplyChainBookings(
      owners: $businessUnitIds
      plannedShippingDateBefore: $plannedShippingDateBefore
      plannedShippingDateAfter: $plannedShippingDateAfter
      status: $status
    ) {
      edges {
        node {
          id
          plannedShippingDate
          status
          numberOfHead
        }
      }
    }
  }
`);
